import React from 'react';
import useStores from '@stores/useStores';
import { apiAuthCaller } from '@services/apiCaller';
import Loading from '@ui-library/Loading';
import { LabelLarge, Subtext } from '@ui-library/typography';
import { RadioChangeEvent } from 'antd';
import _, { get } from 'lodash';
import { FixMe } from '@utils/types';
import { Flex } from '@ui-library/misc';
import RadioGroup from '@ui-library/radio';
import radioClasses from '@components/workflows/steps/selectTransformation/selectTransformation.module.scss';
import { ExistingAccounts } from '@components/sources/source/styles';
import AccountActions from './accountActions';
import WarehouseSettings from '../warehouseSettings';
import { isValidationLoadingType } from '../../addSource';
import { warehouseList } from '../warehouseSourceList/warehouseSettings';
import AccountSummary from './accountSummary';
import classes from '../common.module.scss';

export interface IAccount {
  id: string;
  userId: string;
  role: string;
  skipValidation?: boolean;
  createdAt: string;
  updatedAt: string;
  name?: string;
  options?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  workspaceId?: string;
}

interface Props {
  role: string;
  selectedAccount?: string;
  hasAccounts?: (hasAccount: boolean) => void;
  onClick: (id: string, accountInfo: FixMe) => void;
  isValidationLoading?: isValidationLoadingType;
  isValidationDisable?: boolean;
}

const AuthAccounts = ({
  role,
  onClick,
  hasAccounts,
  selectedAccount,
  isValidationLoading,
  isValidationDisable,
}: Props) => {
  const { userStore } = useStores();
  const [accounts, setAccounts] = React.useState<IAccount[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isViewMode, setViewMode] = React.useState(false);
  const fetchAccounts = React.useCallback(() => {
    setLoading(true);
    apiAuthCaller(userStore.token)
      .get(`/warehouseSources/accounts/${role}`)
      .then((accounts) => {
        setAccounts(accounts.data);
        if (hasAccounts) {
          hasAccounts(!!accounts.data?.length);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [role]);

  const refreshAccounts = React.useCallback(async () => {
    await fetchAccounts();
    onClick('', undefined);
  }, [role]);
  React.useEffect(() => {
    fetchAccounts();
    if (selectedAccount) {
      apiAuthCaller(userStore.token)
        .get(`/warehouseSources/accounts/${role}`)
        .then((accounts) => {
          const accountInfo = accounts.data.find(
            (currAcc: IAccount) => currAcc.id === selectedAccount,
          );
          onClick(selectedAccount, accountInfo);

          setAccounts(accounts.data);
          if (hasAccounts) {
            hasAccounts(!!accounts.data?.length);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [role]);

  if (loading) {
    return <Loading />;
  }
  if (accounts.length === 0) {
    return null;
  }

  const accountDetails = accounts.find((account: IAccount) => account.id === selectedAccount);
  const accountSummaryFields = get(warehouseList, role.toUpperCase())[0].fields.filter(
    (field: { addInAccountSummary: boolean }) => field.addInAccountSummary,
  );

  const onSelect = (e: RadioChangeEvent) => {
    const { value } = e.target;
    const account = accounts.find((acc) => acc.id === value);
    if (value !== selectedAccount && account) {
      const { id, ...accountInfo } = account;
      onClick(value, accountInfo);
    }
  };

  const allAccounts = _.orderBy(accounts, 'createdAt', 'desc');

  return (
    <div className={classes.auth_accounts__container}>
      <LabelLarge className={classes.auth_accounts__header}>Use Existing Credentials</LabelLarge>
      <AccountActions
        role={role}
        refreshAccounts={refreshAccounts}
        selectedAccountId={selectedAccount}
        setViewMode={setViewMode}
        isViewMode={isViewMode}
        validationState={isValidationLoading}
        hideDelete={allAccounts.length < 2}
      />
      {isViewMode ? (
        <WarehouseSettings
          initialSettings={{
            ...accountDetails?.options,
            rudder_account_name: accountDetails?.name,
          }}
          sourceDefinition={{ name: role }}
          disabled
        />
      ) : (
        <ExistingAccounts>
          <Flex alignItems="center">
            <RadioGroup
              onChange={onSelect}
              className={radioClasses.existing_items}
              value={selectedAccount}
              disabled={
                isValidationLoading === isValidationLoadingType.loading && !isValidationDisable
              }
              items={allAccounts.map((acc) => ({
                id: acc.id,
                element: (
                  <Flex flexDirection="column">
                    <Subtext>{acc.name}</Subtext>
                    <AccountSummary
                      fields={accountSummaryFields}
                      info={{ ...acc?.options, createdAt: acc.createdAt }}
                    />
                  </Flex>
                ),
                className: `${radioClasses.item}  ${
                  selectedAccount === acc.id ? radioClasses.selected : ''
                }`,
              }))}
            />
          </Flex>
        </ExistingAccounts>
      )}
    </div>
  );
};

export default AuthAccounts;
